/*
 * The tab row (`APP-016`, `APP-017`), drawn from `contracts/design/app-desk/`.
 *
 * In normal flow directly beneath the masthead, which is the sticky one. A second sticky bar
 * would have to clear the first, and the masthead's height is set by its contents rather than
 * fixed — a magic offset here would be wrong at the first width that wrapped it. The
 * acceptance criterion is that submission is reachable in first view without scrolling past
 * the sections above it, and the top of the page satisfies that at both widths.
 */
.nav {
  border-block-end: 1px solid var(--border);
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(8px);
}

/* One child since `APP-017` took `Submit a deal` to the masthead: the tabs start the row and
 * nothing balances the far edge, so there is nothing left for `space-between` to space. */
.inner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.tabs {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-inline-size: 0;
  /* `base.css` zeroes list margin and padding for every repo but leaves `list-style` alone,
   * so a list that is not read as prose clears its own marker (`APP-018`). `role="list"`
   * on the element keeps the semantics Safari drops along with the marker. */
  list-style: none;
  /* Four tabs do not fit 390 in one row; the strip scrolls rather than wrapping into a
   * second row that would push the desk down on every phone. */
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

/*
 * The canvas draws the tab at 48px with a 2px accent underline on the current one. The 44px
 * minimum is already cleared at 48px, so nothing here is overruled by
 * `contracts/DESIGN-TOKENS.md` § Control metrics.
 */
.tab {
  display: inline-flex;
  align-items: center;
  min-block-size: 3rem;
  padding-inline: var(--space-3);
  border-block-end: 2px solid transparent;
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-decoration: none;
  white-space: nowrap;
}

.tab:hover {
  color: var(--text);
}

/*
 * The focus ring turns inward on a tab, and only on a tab (`APP-018`).
 *
 * `base.css` gives every control a ring 2px *outside* its box, which is right everywhere the
 * control has room around it. `.tabs` is a horizontal scroll container, so an outward ring on
 * the first or last tab is clipped by the very overflow that lets the strip scroll at 390 —
 * the ring is drawn and the founder never sees it. The canvas draws these with
 * `outline-offset:-2px` for the same reason; this is the one place the shared treatment does
 * not fit, and it is narrowed rather than the global rule being loosened.
 */
.tab:focus-visible {
  outline-offset: -2px;
}

/*
 * The current tab is told apart by weight, colour *and* the accent underline, and it also
 * carries `aria-current`. `contracts/DESIGN-TOKENS.md` § Accessibility forbids status by
 * colour alone, and which view you are on is status.
 */
.current {
  border-block-end-color: var(--accent);
  color: var(--text);
  font-weight: 600;
}

/* The short label below 48rem, the full one above. Both are aria-hidden; the accessible
 * name comes from the visually-hidden full label, so it does not change with the viewport. */
.full {
  display: none;
}

@media (min-width: 48rem) {
  .short {
    display: none;
  }

  .full {
    display: inline;
  }
}
